import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Pencil, Trash2, Plus, X } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomPagination from '../../components/common/pagination';
import SearchBar from '../../components/SearchBar';
import DeleteModal from '../../components/deleteModal';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import EmojiTextField from '../../components/common/EmojiTextField';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { translateText } from '../../utils/translateText';
import { LANGUAGES } from '../../constants/languages';
import {
    createNotification,
    getNotifications,
    updateNotification,
    deleteNotification,
    deleteMultipleNotifications,
} from '../../api/notificationapi';
import CustomCheckbox from '../../components/common/CustomCheckbox';

const LIMIT = 15;

// enTitle, hiTitle ... / enDescription, hiDescription ...
const getTitleField = (code) => `${code}Title`;
const getDescriptionField = (code) => `${code}Description`;

const initialFormState = LANGUAGES.reduce((acc, lang) => {
    acc[getTitleField(lang.code)] = '';
    acc[getDescriptionField(lang.code)] = '';
    return acc;
}, {});

const Notification = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [formData, setFormData] = useState(initialFormState);
    const [editId, setEditId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // jya ids nu background translation chalu hoy
    const [translatingIds, setTranslatingIds] = useState([]);

    const [deleteIds, setDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);

    // edit vakhte original English yaad rakhva (change thayu to j retranslate)
    const editOriginalRef = useRef({ enTitle: '', enDescription: '' });

    const hasActiveFilters = Boolean(searchInput.trim());
    const getId = (item) => item._id || item.id;

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const loadItems = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getNotifications({ page: currentPage, limit: LIMIT, search });
            setItems(res?.data || []);
            setTotalPages(res?.totalPages || 1);
            setTotalItems(res?.total || 0);
            setSelectedIds([]);
        } catch (error) {
            toast.error('Failed to load notifications!');
        } finally {
            setLoading(false);
        }
    }, [currentPage, search]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleClearAllFilters = () => {
        setSearchInput('');
        setSearch('');
        setCurrentPage(1);
    };

    /* ---------------- selection ---------------- */

    const allSelected = items.length > 0 && items.every((item) => selectedIds.includes(getId(item)));

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? [] : items.map(getId));
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /* ---------------- modal ---------------- */

    const openAddModal = () => {
        setModalMode('add');
        setFormData(initialFormState);
        setEditId(null);
        setErrors({});
        editOriginalRef.current = { enTitle: '', enDescription: '' };
        setIsModalOpen(true);
    };

    const openEditModal = (item) => {
        setModalMode('edit');

        // badhi languages form state ma rakhi chhe (UI ma dekhati nathi),
        // jethi update vakhte existing translations na jay
        const next = {};
        LANGUAGES.forEach(({ code }) => {
            const titleField = getTitleField(code);
            const descField = getDescriptionField(code);
            next[titleField] = item[titleField] || '';
            next[descField] = item[descField] || '';
        });

        editOriginalRef.current = {
            enTitle: (item.enTitle || '').trim(),
            enDescription: (item.enDescription || '').trim(),
        };

        setFormData(next);
        setEditId(getId(item));
        setErrors({});
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (submitting) return;
        setIsModalOpen(false);
        setFormData(initialFormState);
        setEditId(null);
        setErrors({});
    };

    const clearFieldError = (field) => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    /* ---------------- background translation (backend update only) ---------------- */

    // English thi badhi bija languages ek pachi ek translate kare.
    // Darek language aave etle backend ma update thay. UI ma kai batavvanu nathi.
    const translateInBackground = async (id, base) => {
        const targets = LANGUAGES.filter((l) => l.code !== 'en');
        const failed = [];
        let acc = { ...base };

        setTranslatingIds((prev) => [...prev, id]);

        try {
            for (const lang of targets) {
                try {
                    const [title, description] = await Promise.all([
                        translateText(base.enTitle, lang.code),
                        translateText(base.enDescription, lang.code),
                    ]);

                    acc = {
                        ...acc,
                        [getTitleField(lang.code)]: title,
                        [getDescriptionField(lang.code)]: description,
                    };

                    await updateNotification(id, acc);
                } catch (error) {
                    failed.push(lang.name);
                    // record delete thai gayo hoy to aagal na jao
                    if (error?.response?.status === 404) break;
                }
            }
        } finally {
            setTranslatingIds((prev) => prev.filter((x) => x !== id));
        }

        // if (failed.length === 0) {
        //     toast.success('Translation completed for all languages!');
        // } else {
        //     toast.warning(`Could not translate: ${failed.join(', ')}. Edit the notification and save to retry.`);
        // }
    };

    /* ---------------- submit ---------------- */

    // fakt English validate thay chhe
    const validate = () => {
        const errs = {};
        if (!formData.enTitle.trim()) errs.enTitle = 'English title is required';
        if (!formData.enDescription.trim()) errs.enDescription = 'English description is required';
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const enTitle = formData.enTitle.trim();
        const enDescription = formData.enDescription.trim();

        try {
            setSubmitting(true);

            let targetId = null;
            let shouldTranslate = false;
            let baseData = null;

            if (modalMode === 'add') {
                // pehla fakt English sathe create
                const res = await createNotification({ enTitle, enDescription });
                const created = res?.data ?? res;
                targetId = created?._id || created?.id || null;
                baseData = { ...initialFormState, enTitle, enDescription };
                shouldTranslate = true;
                // toast.success('Notification added! Translating in background...');
            } else {
                const englishChanged =
                    enTitle !== editOriginalRef.current.enTitle ||
                    enDescription !== editOriginalRef.current.enDescription;

                baseData = { ...formData, enTitle, enDescription };
                await updateNotification(editId, baseData);
                targetId = editId;
                shouldTranslate = englishChanged;
                toast.success('Notification updated successfully!');
            }

            // modal turant close
            setIsModalOpen(false);
            setFormData(initialFormState);
            setEditId(null);
            setErrors({});

            if (modalMode === 'add' && currentPage !== 1) {
                setCurrentPage(1);
            } else {
                loadItems();
            }

            if (shouldTranslate) {
                if (targetId) {
                    // await nathi karyu => background ma chale
                    translateInBackground(targetId, baseData);
                } else {
                    // toast.warning('Created, but could not start translation (id not returned by API).');
                }
            }
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                (modalMode === 'add' ? 'Failed to add notification!' : 'Failed to update notification!');
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------------- delete (single + multiple) ---------------- */

    const confirmDelete = (id) => setDeleteIds([id]);
    const confirmDeleteSelected = () => setDeleteIds(selectedIds);
    const cancelDelete = () => {
        if (!deleting) setDeleteIds([]);
    };

    const handleDelete = async () => {
        if (deleteIds.length === 0) return;

        try {
            setDeleting(true);

            if (deleteIds.length === 1) {
                await deleteNotification(deleteIds[0]);
                toast.success('Notification deleted successfully!');
            } else {
                const res = await deleteMultipleNotifications(deleteIds);
                toast.success(res?.message || `${deleteIds.length} notifications deleted successfully!`);
            }

            const deletedWholePage = deleteIds.length >= items.length;
            setDeleteIds([]);

            if (deletedWholePage && currentPage > 1) {
                setCurrentPage((p) => p - 1);
            } else {
                loadItems();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete!');
        } finally {
            setDeleting(false);
        }
    };

    /* ---------------- render ---------------- */

    const headerCellBase =
        'py-3 px-2 text-center font-medium text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700';
    const bodyCellBase =
        'py-3 px-3 align-middle text-gray-700 dark:text-gray-300 border-r border-b border-gray-200 dark:border-gray-700';
    const colCount = 5;

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] min-h-0 overflow-hidden">
            {/* Header */}
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
                <PageBreadcrumb pageTitle="Notifications" />

                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={openAddModal}
                        className="inline-flex items-center gap-2 justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white"
                    >
                        <Plus size={16} /> Add Notification
                    </button>
                </div>
            </div>

            {/* Table area */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="flex h-full min-h-0 flex-col rounded-2xl border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
                    <div className="flex shrink-0 items-center justify-between">
                        <SearchBar
                            value={searchInput}
                            onChange={setSearchInput}
                            placeholder="Search in any language..."
                            showClearAll={hasActiveFilters}
                            onClearAll={handleClearAllFilters}
                        />

                        {selectedIds.length > 0 && (
                            <button
                                onClick={confirmDeleteSelected}
                                className="inline-flex items-center gap-2 justify-center rounded-lg bg-red-500 px-4 py-2 mx-3 text-sm font-medium text-white hover:bg-red-600"
                            >
                                <Trash2 size={16} /> Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6 pt-4">
                        <div className="h-full min-h-0 rounded-lg border dark:border-gray-800 overflow-auto">
                            <table className="w-full">
                                <thead className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-white/[0.05] dark:bg-gray-900">
                                    <tr>
                                        <th className={`${headerCellBase} w-12 bg-white dark:bg-gray-900`}>
                                            <CustomCheckbox
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                disabled={items.length === 0}
                                            />
                                        </th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Index</th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Title</th>
                                        <th className={`${headerCellBase} bg-white dark:bg-gray-900`}>Description</th>
                                        <th className="py-3 px-2 text-center font-medium text-gray-500 bg-white dark:text-gray-400 dark:bg-gray-900">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : items.length === 0 ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                No data found.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => {
                                            const id = getId(item);
                                            const title = item.enTitle;
                                            const description = item.enDescription;

                                            return (
                                                <tr key={id} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                                                    <td className={`${bodyCellBase} text-center`}>
                                                        <CustomCheckbox
                                                            checked={selectedIds.includes(id)}
                                                            onChange={() => toggleSelectOne(id)}
                                                        />
                                                    </td>

                                                    <td className={`${bodyCellBase} text-center`}>
                                                        {(currentPage - 1) * LIMIT + index + 1}
                                                    </td>

                                                    {/* Title (English) */}
                                                    <td className={`${bodyCellBase} min-w-[220px]`}>
                                                        <div className="flex items-center justify-between gap-2 text-center">
                                                            <span className="flex-1 whitespace-normal break-words text-center text-gray-800 dark:text-white">
                                                                {title || '—'}
                                                            </span>

                                                            {title && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyToClipboard(title, 'Title')}
                                                                    title="Copy title"
                                                                    className="shrink-0 text-gray-400 hover:text-brand-500 transition-colors"
                                                                >
                                                                    <Copy size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Description (English) */}
                                                    <td className={`${bodyCellBase} min-w-[260px]`}>
                                                        <div className="flex items-center justify-between gap-2 text-center">
                                                            <span className="flex-1 whitespace-normal break-words text-center text-gray-800 dark:text-white">
                                                                {description || '—'}
                                                            </span>

                                                            {description && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyToClipboard(description, 'Description')}
                                                                    title="Copy description"
                                                                    className="shrink-0 text-gray-400 hover:text-brand-500 transition-colors"
                                                                >
                                                                    <Copy size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-2 text-center align-middle border-b border-gray-200 dark:border-gray-700">
                                                        <div className="flex items-center justify-center gap-3">
                                                            <button onClick={() => openEditModal(item)} title="Edit" className="text-brand-600">
                                                                <Pencil size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => confirmDelete(id)}
                                                                title="Delete"
                                                                className="text-red-500 hover:text-red-600"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="shrink-0 w-full border-t dark:bg-gray-900 dark:border-gray-700 mt-0">
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={LIMIT}
                    totalItems={totalItems}
                />
            </div>

            {/* Add/Edit Modal (outside click thi close nahi thay) */}
            {isModalOpen && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/50 px-4">
                    <div className="relative w-full max-w-lg max-h-[95vh] overflow-y-auto rounded-xl bg-white p-6 dark:bg-gray-900">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {modalMode === 'add' ? 'Add Notification' : 'Edit Notification'}
                            </h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={submitting}
                                title="Close"
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 dark:text-gray-400 dark:hover:bg-white/[0.05] dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <EmojiTextField
                                label="Title"
                                required
                                value={formData.enTitle}
                                onChange={(v) => handleFieldChange('enTitle', v)}
                                error={errors.enTitle}
                                disabled={submitting}
                            />

                            <EmojiTextField
                                label="Description"
                                required
                                value={formData.enDescription}
                                onChange={(v) => handleFieldChange('enDescription', v)}
                                error={errors.enDescription}
                                disabled={submitting}
                            />

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={submitting}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                                >
                                    {submitting ? 'Saving...' : modalMode === 'add' ? 'Create' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteModal
                isOpen={deleteIds.length > 0}
                value={deleteIds.length > 1 ? `${deleteIds.length} notifications` : 'notification'}
                isDeleting={deleting}
                onClose={cancelDelete}
                onConfirm={handleDelete}
            />

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default Notification;